import { inject, Injectable, signal } from '@angular/core';
import { BreakpointObserver, Breakpoints as MatBreakpoints, BreakpointState } from '@angular/cdk/layout';
import { NGXLogger } from 'ngx-logger';
import { Nullable, Optional } from '../../shared/shared.types';

export const Breakpoints = {
  XSmall: MatBreakpoints.XSmall,
  Small: '(min-width: 600px) and (max-width: 839px)',
  Medium: '(min-width: 840px) and (max-width: 1279px)',
  Large: '(min-width: 1280px)',
};

export const BreakpointRanges = {
  XSmall: { minWidth: 0, maxWidth: 599 },
  Small: { minWidth: 600, maxWidth: 839 },
  Medium: { minWidth: 840, maxWidth: 1279 },
  Large: { minWidth: 1280 },
};

export const MOBILE_DEVICE = 'mobile';
export const DESKTOP_DEVICE = 'desktop';

@Injectable({
  providedIn: 'root',
})
export class ScreenService {
  private readonly _deviceSignal = signal<string>(MOBILE_DEVICE);
  private readonly _breakpoint = signal<string>(Breakpoints.XSmall);
  private $size?: Optional<string>;
  private $standalone = false;
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly logger = inject(NGXLogger);

  readonly deviceSignal = this._deviceSignal.asReadonly();
  readonly breakpoint = this._breakpoint.asReadonly();

  constructor() {
    const prop = 'standalone';
    if ((window.navigator as Navigator & { standalone?: boolean })[prop]) {
      this.logger.debug('navigator is ins standalone mode');
      this.$standalone = true;
    } else if (
      window.matchMedia('all and (display-mode: standalone)').matches
    ) {
      this.logger.debug('display mode matches standalone');
      this.$standalone = true;
    }

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .subscribe((result) => {
        if (result.matches) {
          this._breakpoint.set(this.determineBreakpoint(result) ?? Breakpoints.XSmall);
          this._deviceSignal.set(MOBILE_DEVICE);
          if (this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
            this.$size = 'xs';
          } else {
            this.$size = 'sm';
          }
        }
      });
    this.breakpointObserver
      .observe([Breakpoints.Medium, Breakpoints.Large])
      .subscribe((result) => {
        if (result.matches) {
          this._breakpoint.set(this.determineBreakpoint(result) ?? Breakpoints.Large);
          this._deviceSignal.set(DESKTOP_DEVICE);
          if (this.breakpointObserver.isMatched(Breakpoints.Medium)) {
            this.$size = 'md';
          } else {
            this.$size = 'lg';
          }
        }
      });
  }

  get device(): string {
    return this.deviceSignal();
  }

  get size(): Nullable<string> {
    return this.$size ?? null;
  }

  get isStandalone(): boolean {
    return this.$standalone;
  }

  get isMobile(): boolean {
    return this.deviceSignal() === MOBILE_DEVICE;
  }

  get isDesktop(): boolean {
    return this.deviceSignal() === DESKTOP_DEVICE;
  }

  private determineBreakpoint(result: BreakpointState) {
    return Object.values(Breakpoints).find((query) => {
      if (result.breakpoints[query]) {
        return query;
      }
    });
  }
}
